import { create } from 'zustand';
import type { PlatformAdapter } from '@blockbattle/platform-adapters';
import type { AuthSession, GameMode, RunResultResponse } from '@blockbattle/shared';
import { configureAudioPreferences, setEffectsEnabled, setMusicEnabled } from './audio.js';
import { readResultCheckpoint, saveResultCheckpoint } from './result-checkpoint.js';

const savedSound =
  typeof localStorage === 'undefined' ? true : localStorage.getItem('bb_sound') !== 'off';
const savedMusic =
  typeof localStorage === 'undefined'
    ? true
    : localStorage.getItem('bb_music') !== 'off' && localStorage.getItem('bb_sound') !== 'off';
const savedHaptics =
  typeof localStorage === 'undefined' ? true : localStorage.getItem('bb_haptics') !== 'off';
const savedReducedMotion =
  typeof localStorage === 'undefined' ? false : localStorage.getItem('bb_reduced_motion') === 'on';
configureAudioPreferences(savedSound, savedMusic);

interface AppStore {
  adapter: PlatformAdapter | null;
  session: AuthSession | null;
  lastResult:
    | (RunResultResponse & {
        runId: string;
        mode: GameMode;
        duelCode?: string;
      })
    | null;
  sound: boolean;
  music: boolean;
  haptics: boolean;
  reducedMotion: boolean;
  setAdapter(adapter: PlatformAdapter): void;
  setSession(session: AuthSession): void;
  setTutorialComplete(complete: boolean): void;
  setLastResult(
    result: RunResultResponse & { runId: string; mode: GameMode; duelCode?: string },
  ): void;
  toggleSound(): void;
  toggleMusic(): void;
  toggleAudio(): void;
  toggleHaptics(): void;
  toggleReducedMotion(): void;
}

export const useAppStore = create<AppStore>((set, get) => ({
  adapter: null,
  session: null,
  lastResult: null,
  sound: savedSound,
  music: savedMusic,
  haptics: savedHaptics,
  reducedMotion: savedReducedMotion,
  setAdapter: (adapter) => set({ adapter }),
  setSession: (session) =>
    set({
      session,
      lastResult:
        get().session?.user.internalId === session.user.internalId
          ? get().lastResult
          : session.user.internalId
            ? readResultCheckpoint(session.user.internalId)
            : null,
    }),
  setTutorialComplete: (tutorialComplete) =>
    set((state) =>
      state.session
        ? { session: { ...state.session, user: { ...state.session.user, tutorialComplete } } }
        : state,
    ),
  setLastResult: (lastResult) => {
    const userId = get().session?.user.internalId;
    if (userId) saveResultCheckpoint(userId, lastResult);
    set({ lastResult });
  },
  toggleSound: () =>
    set((state) => {
      const sound = !state.sound;
      setEffectsEnabled(sound);
      localStorage.setItem('bb_sound', sound ? 'on' : 'off');
      return { sound };
    }),
  toggleMusic: () =>
    set((state) => {
      const music = !state.music;
      setMusicEnabled(music);
      localStorage.setItem('bb_music', music ? 'on' : 'off');
      return { music };
    }),
  toggleAudio: () =>
    set((state) => {
      const enabled = !(state.sound || state.music);
      setEffectsEnabled(enabled);
      setMusicEnabled(enabled);
      localStorage.setItem('bb_sound', enabled ? 'on' : 'off');
      localStorage.setItem('bb_music', enabled ? 'on' : 'off');
      return { sound: enabled, music: enabled };
    }),
  toggleHaptics: () =>
    set((state) => {
      const haptics = !state.haptics;
      localStorage.setItem('bb_haptics', haptics ? 'on' : 'off');
      return { haptics };
    }),
  toggleReducedMotion: () =>
    set((state) => {
      const reducedMotion = !state.reducedMotion;
      localStorage.setItem('bb_reduced_motion', reducedMotion ? 'on' : 'off');
      return { reducedMotion };
    }),
}));
